<template>
    <div class="advanced-view">
        <!-- Filters -->
        <div class="advanced-filters">
            <div class="filter-group">
                <label>Filter by Module:</label>
                <select :value="selectedModule" @change="$emit('update:selectedModule', $event.target.value)">
                    <option value="all">All Modules</option>
                    <option value="M1">Module 1</option>
                    <option value="M2">Module 2</option>
                    <option value="M3">Module 3</option>
                    <option value="M4">Module 4</option>
                    <option value="M5">Module 5</option>
                    <option value="M6">Module 6</option>
                    <option value="M7">Module 7</option>
                    <option value="M8">Module 8</option>
                </select>
            </div>

            <div class="filter-group">
                <label>Sort by:</label>
                <select :value="sortBy" @change="$emit('update:sortBy', $event.target.value)">
                    <option value="accuracy">Accuracy</option>
                    <option value="attempts">Attempts</option>
                    <option value="average_score">Average Score</option>
                </select>
            </div>

            <div class="filter-group">
                <label>Order:</label>
                <select :value="sortOrder" @change="$emit('update:sortOrder', $event.target.value)">
                    <option value="asc">Ascending</option>
                    <option value="desc">Descending</option>
                </select>
            </div>

            <div class="filter-group">
                <label>Search:</label>
                <input
                    type="text"
                    :value="searchQuery"
                    @input="$emit('update:searchQuery', $event.target.value)"
                    placeholder="Search topics..."
                    class="search-input"
                />
            </div>
        </div>

        <!-- Table -->
        <div class="table-container">
            <table class="performance-table">
                <thead>
                    <tr>
                        <th>Topic</th>
                        <th>Module</th>
                        <th @click="$emit('sort', 'accuracy')" class="sortable">
                            Accuracy
                            <span v-if="sortBy === 'accuracy'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
                        </th>
                        <th @click="$emit('sort', 'attempts')" class="sortable">
                            Attempts
                            <span v-if="sortBy === 'attempts'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
                        </th>
                        <th>Correct</th>
                        <th @click="$emit('sort', 'average_score')" class="sortable">
                            Avg Score
                            <span v-if="sortBy === 'average_score'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
                        </th>
                    </tr>
                </thead>
                <tbody :key="selectedModule">
                    <tr
                        v-for="(perf, topic) in filteredTopics"
                        :key="topic"
                        class="table-row"
                    >
                        <td class="topic-cell">{{ getTopicLabel(topic) }}</td>
                        <td class="module-cell">{{ getModuleForTopic(topic) }}</td>
                        <td :class="getAccuracyClass(perf.accuracy)">
                            {{ perf.accuracy.toFixed(1) }}%
                        </td>
                        <td>{{ perf.attempts }}</td>
                        <td>{{ perf.correct }}</td>
                        <td>{{ perf.average_score.toFixed(2) }}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import { getModuleForTopic, getTopicLabel } from '@/utils/topicHelpers';

export default {
    name: 'PerformanceAdvancedView',
    props: {
        filteredTopics: {
            type: Object,
            required: true
        },
        selectedModule: {
            type: String,
            required: true
        },
        sortBy: {
            type: String,
            required: true
        },
        sortOrder: {
            type: String,
            required: true
        },
        searchQuery: {
            type: String,
            required: true
        }
    },
    emits: ['update:selectedModule', 'update:sortBy', 'update:sortOrder', 'update:searchQuery', 'sort'],
    setup() {
        const getAccuracyClass = (accuracy) => {
            if (accuracy >= 80) return 'accuracy-high';
            if (accuracy >= 60) return 'accuracy-medium';
            return 'accuracy-low';
        };

        return {
            getModuleForTopic,
            getTopicLabel,
            getAccuracyClass
        };
    }
};
</script>

<style scoped>
.advanced-view {
    margin-top: 20px;
}

.advanced-filters {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 15px;
    margin-bottom: 20px;
    padding: 20px;
    background: var(--bg-secondary);
    border: 1px solid var(--accent);
}

.filter-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.filter-group label {
    color: var(--text-secondary);
    font-size: 13px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1px;
}

.filter-group select,
.search-input {
    padding: 10px;
    background: rgba(var(--bg-primary-rgb), 0.6);
    color: var(--text-primary);
    border: 1px solid var(--accent);
    font-size: 14px;
    font-family: 'Space Grotesk';
    transition: border-color 0.3s;
}

.filter-group select:focus,
.search-input:focus {
    outline: none;
    border-color: var(--text-secondary);
}

.table-container {
    overflow-x: auto;
    border: 1px solid var(--accent);
}

.performance-table {
    width: 100%;
    border-collapse: collapse;
}

.performance-table thead {
    background: var(--bg-primary);
    position: sticky;
    top: 0;
    z-index: 10;
}

.performance-table th {
    padding: 15px;
    text-align: left;
    color: var(--text-secondary);
    font-weight: 700;
    font-size: 14px;
    text-transform: uppercase;
    letter-spacing: 1px;
    border-bottom: 2px solid var(--text-secondary);
}

.performance-table th.sortable {
    cursor: pointer;
    user-select: none;
    transition: background 0.2s;
}

.performance-table th.sortable:hover {
    background: rgba(254, 231, 179, 0.15);
}

.performance-table td {
    padding: 15px;
    color: var(--text-primary);
    border-bottom: 1px solid var(--accent);
}

.table-row {
    transition: background 0.2s;
}

tr:nth-child(even) {
    background-color: rgba(var(--accent-rgb), 0.1);
}

.table-row:hover {
    background: var(--bg-secondary);
}

.topic-cell {
    font-weight: 600;
}

.module-cell {
    color: var(--text-secondary);
    font-size: 13px;
}

.accuracy-high {
    color: #4caf50;
    font-weight: 700;
}

.accuracy-medium {
    color: #ff9800;
    font-weight: 700;
}

.accuracy-low {
    color: #f44336;
    font-weight: 700;
}
</style>